// Русские названия и описания для UI автономности (страница /agent, tooltip'ы, TG-карточки).
// Держим отдельно от autonomy-matrix.ts, чтобы бэк-логика не тянула тексты локали.

import type { Intent } from '../types/intent';
import type { ToolName } from '../types/tool';
import type { AutonomyMode, AutonomyPreset, ToolClass } from './autonomy-matrix';

export interface ToolLabel {
  /** Короткое имя инструмента для UI-таблицы. */
  title: string;
  /** Описание что делает - для tooltip'а и подсказки оператору. */
  description: string;
}

export const TOOL_LABELS_RU: Record<ToolName, ToolLabel> = {
  get_availability: {
    title: 'Проверить доступность',
    description: 'Смотрит ваш календарь: свободна ли квартира на нужные даты.',
  },
  quote_price: {
    title: 'Рассчитать цену',
    description: 'Считает цену по вашим правилам, а не выдумывает её.',
  },
  propose_upsell: {
    title: 'Предложить допуслугу',
    description: 'Предлагает ранний заезд, поздний выезд или лишнюю ночь по вашей цене.',
  },
  confirm_upsell: {
    title: 'Подтвердить допуслугу',
    description: 'Фиксирует, что гость согласился, и ставит это в план.',
  },
  update_booking_internal: {
    title: 'Изменить бронь у нас',
    description: 'Меняет даты, число гостей или статус в вашем календаре.',
  },
  request_external_booking_update: {
    title: 'Изменить бронь у площадки',
    description: 'Просит площадку (Авито и др.) отразить изменение брони.',
  },
  send_checkin_instructions: {
    title: 'Отправить инструкцию заселения',
    description: 'Пришлёт гостю адрес, код замка и Wi-Fi. Только после оплаты и не раньше чем за 3 часа до заезда.',
  },
  create_cleaning_task: {
    title: 'Создать задачу клинерам',
    description: 'После выезда гостя ставит уборку в расписание клинеров.',
  },
  reschedule_cleaning_task: {
    title: 'Перенести уборку',
    description: 'Сдвигает уборку, если поменялся выезд или заезд следующего гостя.',
  },
  create_incident: {
    title: 'Создать инцидент',
    description: 'Заводит заявку - сломался замок, авария, шум - и запускает таймер для мастера.',
  },
  notify_master: {
    title: 'Уведомить мастера',
    description: 'Пишет мастеру, что сломалось и куда ехать.',
  },
  request_review: {
    title: 'Запросить отзыв',
    description: 'После выезда просит гостя оставить отзыв на площадке.',
  },
  escalate: {
    title: 'Передать оператору',
    description: 'Останавливает автоответы и передаёт диалог вам - в раздел «Внимание».',
  },
  save_kb_answer: {
    title: 'Сохранить ответ в базу знаний',
    description: 'Запоминает ваш ответ - в следующий раз Smena ответит сама.',
  },
};

/**
 * Предметные области - то, как работу видит управляющий: «заезд и доступ», «быт и техника».
 * Класс инструмента (чтение / отправка / необратимое) объясняет ДЕФОЛТ из пресета, а область
 * отвечает на вопрос «про что это».
 *
 * Один и тот же список работает и для инструментов, и для тем вопросов гостя - оператор
 * видит одни и те же названия в обеих таблицах. Покрытие полное: каждый интент из INTENTS
 * и каждый инструмент из TOOL_WHITELIST лежит ровно в одной области.
 */
export const AREA_ORDER = [
  'booking',
  'checkin',
  'checkout',
  'utilities',
  'rules',
  'location',
  'money',
  'talk',
] as const;

export type AutonomyArea = (typeof AREA_ORDER)[number];

export const AREA_LABEL_RU: Record<AutonomyArea, string> = {
  booking: 'Бронь и даты',
  checkin: 'Заезд и доступ',
  checkout: 'Выезд и уборка',
  utilities: 'Быт и техника',
  rules: 'Правила проживания',
  location: 'Локация и парковка',
  money: 'Деньги и доплаты',
  talk: 'Общение и жалобы',
};

export const TOOL_AREA: Record<ToolName, AutonomyArea> = {
  get_availability: 'booking',
  quote_price: 'booking',
  update_booking_internal: 'booking',
  request_external_booking_update: 'booking',
  send_checkin_instructions: 'checkin',
  create_cleaning_task: 'checkout',
  reschedule_cleaning_task: 'checkout',
  create_incident: 'utilities',
  notify_master: 'utilities',
  propose_upsell: 'money',
  confirm_upsell: 'money',
  request_review: 'talk',
  escalate: 'talk',
  save_kb_answer: 'talk',
};

/** Область для темы вопроса гостя. Ключи - все интенты из INTENTS. */
export const INTENT_AREA: Record<Intent, AutonomyArea> = {
  availability_price: 'booking',
  object_details: 'booking',
  booking_request: 'booking',
  date_change: 'booking',
  extend_stay: 'booking',
  checkin_how: 'checkin',
  checkin_time_change: 'checkin',
  incident_lock: 'checkin',
  checkout_how: 'checkout',
  late_checkout: 'checkout',
  wifi_appliances: 'utilities',
  incident_utilities: 'utilities',
  crib_question: 'utilities',
  conditions: 'rules',
  house_rules: 'rules',
  incident_noise: 'rules',
  incident_noise_neighbors: 'rules',
  local_tips: 'location',
  parking: 'location',
  bargain: 'money',
  refund_cancel: 'money',
  damage: 'money',
  greeting: 'talk',
  complaint: 'talk',
  review_related: 'talk',
  other: 'talk',
  unclear: 'talk',
};

export const CLASS_LABEL_RU: Record<ToolClass, string> = {
  read: 'Только смотрит',
  outbound: 'Пишет гостю и ставит задачи',
  irreversible: 'Меняет то, что сложно вернуть',
};

export const CLASS_HINT_RU: Record<ToolClass, string> = {
  read: 'Ничего не меняет - только читает ваши данные.',
  outbound: 'Пишет гостю или ставит задачу внутри системы. Всё это можно отменить.',
  irreversible: 'Уходит наружу - мастеру или на площадку. Отменить уже сложно.',
};

// Подписи пишем словами владельца, а не разработчика: панель открывает управляющий, а не
// инженер, и «auto_notify» или «HITL-очередь» ему ничего не говорят. Smena женского рода
// (brand/README.md), отсюда «сама».
export const MODE_LABEL_RU: Record<AutonomyMode, string> = {
  auto: 'Сама',
  auto_notify: 'Сама, сообщит',
  confirm: 'Спросит вас',
  off: 'Не делает',
};

export const MODE_HINT_RU: Record<AutonomyMode, string> = {
  auto: 'Smena делает это сама, подтверждать не нужно.',
  auto_notify: 'Smena делает сама и сразу присылает вам сообщение о том, что сделала.',
  confirm: 'Smena подготовит действие и подождёт вашей кнопки. Не ответите за 30 минут - отменится.',
  off: 'Smena этого не делает совсем.',
};

export const PRESET_LABEL_RU: Record<AutonomyPreset, string> = {
  strict: 'Строгий',
  balance: 'Баланс',
  free: 'Свобода',
};

export const PRESET_HINT_RU: Record<AutonomyPreset, string> = {
  strict:
    'Сама Smena только смотрит календарь и считает цену. Всё, что уходит гостю или наружу, ждёт вашей кнопки.',
  balance:
    'Smena сама отвечает гостю и ставит задачи внутри, о каждом действии сообщает. То, что сложно откатить, спрашивает.',
  free:
    'Smena делает всё сама и сообщает вам. Включайте, когда уже доверяете качеству ответов.',
};

export const REASON_LABEL_RU: Record<'override' | 'preset' | 'unknown_tool', string> = {
  override: 'вы изменили',
  preset: 'по умолчанию',
  unknown_tool: 'нет в списке',
};

/**
 * Русские названия и подсказки для интентов - используются в UI автономности per-intent.
 * Ключи - все intent'ы из INTENTS; в UI показываем только whitelisted (см. выше).
 */
export const INTENT_LABEL_RU: Record<string, { title: string; description: string }> = {
  greeting: { title: 'Приветствие', description: 'Гость поздоровался или уточняет, кто отвечает.' },
  availability_price: { title: 'Свободно ли / цена', description: 'Вопрос о свободных датах или стоимости.' },
  object_details: { title: 'Про объект', description: 'Метро, район, планировка, удобства.' },
  booking_request: { title: 'Хочу забронировать', description: 'Гость просит забронировать конкретные даты.' },
  bargain: { title: 'Торг / скидка', description: 'Гость просит скидку или другую цену.' },
  conditions: { title: 'Правила / условия', description: 'Депозит, дети, животные, курение, вечеринки.' },
  checkin_how: { title: 'Как заселиться', description: 'Адрес, код замка, инструкции.' },
  checkin_time_change: { title: 'Перенос заезда', description: 'Гость хочет раньше/позже заселиться.' },
  late_checkout: { title: 'Поздний выезд', description: 'Гость просит выехать позже стандартного времени.' },
  date_change: { title: 'Смена дат', description: 'Гость просит сдвинуть заезд или выезд.' },
  extend_stay: { title: 'Продлить бронь', description: 'Гость хочет остаться ещё на ночь.' },
  wifi_appliances: { title: 'Wi-Fi / техника', description: 'Пароль от Wi-Fi, как включить стиралку или кофеварку.' },
  parking: { title: 'Парковка', description: 'Куда встать, платно/бесплатно.' },
  house_rules: { title: 'Правила дома', description: 'Тихие часы, курение, гости.' },
  local_tips: { title: 'Что рядом', description: 'Кафе, аптека, магазины, транспорт.' },
  incident_lock: { title: 'Инцидент: замок', description: 'Гость не может попасть в квартиру.' },
  incident_utilities: { title: 'Инцидент: коммунальные', description: 'Нет воды, света или тепла, течёт сантехника.' },
  incident_noise: { title: 'Инцидент: шум', description: 'Шум в квартире: техника, ремонт у соседей.' },
  incident_noise_neighbors: { title: 'Соседи шумят', description: 'Гость жалуется на соседей.' },
  complaint: { title: 'Жалоба', description: 'Гость недоволен: грязь, техника, ожидания.' },
  damage: { title: 'Что-то сломал / испортил', description: 'Гость сообщает, что что-то сломано или испорчено.' },
  refund_cancel: { title: 'Возврат / отмена', description: 'Просит вернуть деньги или отменить бронь.' },
  checkout_how: { title: 'Как выехать', description: 'Куда оставить ключи, что закрыть.' },
  review_related: { title: 'Про отзыв', description: 'Гость говорит про отзыв - хороший или плохой.' },
  crib_question: { title: 'Кроватка', description: 'Есть ли детская кроватка.' },
  other: { title: 'Другое', description: 'Всё, что не попало в другие темы.' },
  unclear: { title: 'Непонятно', description: 'Smena не поняла, о чём вопрос.' },
};
